import { defineComponent as _defineComponent } from 'vue'
import BCXAvatar from '@/components/molecules/BCXAvatar.vue';
import BCXItemsWithPoints from '@/components/molecules/BCXItemsWithPoints.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXPoints from '@/components/molecules/BCXPoints.vue';
import BCXUserBadges from '@/components/molecules/BCXUserBadges.vue';
import BCXUserBanner from '@/components/molecules/BCXUserBanner.vue';
import EditIcon from '@/components/molecules/EditIcon.vue';
import ProfileSectionBusyOverlay from '@/views/profile/components/ProfileSectionBusyOverlay.vue';
import IconAvatarEdit from '@/components/svg/avatar-circle.svg?inline';
import MessageIcon from '@/components/svg/icon-message.svg?inline';
import ShareIcon from '@/components/svg/share_cropped.svg?inline';
import UserHistoryIcon from '@/components/svg/user-history.svg?inline';
import UserIcon from '@/components/svg/user.svg?inline';
import FlagIcon from '@/components/svg/flag_outline.svg?inline';
import Reporting from '@/components/voting/Reporting.vue';
import useFormattedDateTime from '@/mixins/useFormattedDateTime';
import useAddressTools from '@/mixins/useAddressTools';
import useI18n from '@/mixins/useI18n';
import useResponsiveness from '@/mixins/useResponsiveness';
import { HtmlTarget } from '@/models/HtmlTarget';
import { BOT_NAMES } from '@/models/Messenger';
import useMessengerState from '@/state/messenger/messengerState';
import useProfileState from '@/state/profile/profileState';
import usePageState from '@/state/pageState';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import { logError } from '@/utils/logger';
import { AxiosError } from 'axios';
import useQuickInfo from '@/state/quickInfo';
import store from '@/store';
import { updateAvatar, updateBasicData } from '@/services/Profile/Api';
import { getCountryCities } from '@/services/Geo/Api';
import buildAcceptedBasicUpdate from '@/views/profile/composables/buildAcceptedBasicUpdate';
import AvatarUpload from '@/views/profile/components/AvatarUpload.vue';
import ProfileAssistantButton from '@/views/profile/components/profile-pdf-upload/ProfileAssistantButton.vue';
import AiSuggestionReviewBar from '@/views/profile/components/profile-pdf-upload/AiSuggestionReviewBar.vue';
import useProfilePdfUploadPopup from '@/views/profile/composables/useProfilePdfUploadPopup';
import useProfilePdfUpload, { ProfilePdfReanalyzeSource } from '@/views/profile/composables/useProfilePdfUpload';
import useProfilePreview from '@/state/profile/profilePreview';
import RecommendUserForProjectsMobileLayer from '@/views/recommendations/components/recommend-user-for-projects/RecommendUserForProjectsMobileLayer.vue';
import RecommendUserForProjectsQuickInfoLayer from '@/views/recommendations/components/recommend-user-for-projects/RecommendUserForProjectsQuickInfoLayer.vue';
import useRecommendations from '@/views/profile/composables/useRecommendations';
import {
  computed, PropType, ref, toRefs
} from 'vue';
import { templateRef } from '@vueuse/core';
import { useRouter } from 'vue2-helpers/vue-router';
import useUser from '@/mixins/useUser';
import UserAvailability from '@/components/UserAvailability.vue';
import useAvailabilityState from '@/state/availabilityState';
import BCXUserBannerSimple from '@/components/molecules/BCXUserBannerSimple.vue';
import { extractCityName } from '@/utils/user';
import { getTextByValue, getValueByText } from '@/utils/selectItemTools';
import normalizeAvatarImage from '@/utils/normalizeAvatarImage';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProfileBasicView',
  props: {
  showContactHistory: {
    type: Boolean,
    default: false
  },
  target: {
    type: String as PropType<HtmlTarget>,
    default: '_self'
  },
  showBadgesLegend: {
    type: Boolean,
    default: false
  }
},
  emits: ['show-contact-history'],
  setup(__props, { emit }) {

const props = __props;



const { showContactHistory, showBadgesLegend } = toRefs(props);



const { setQuickInfoUser, showQuickInfo: showQuickInfoOriginal } = useQuickInfo();
const { availability } = useRecommendations();
const { getFormattedDate } = useFormattedDateTime();
const { isAvailabilityPanelOpen } = useAvailabilityState();
const {
  user, profile, isSelf, toggleEditSection, isQuickInfo, canEdit, isSectionFilled, loadProfile, savedProfile
} = useProfileState();
const {
  isCustomer, isGuest, isEmployee, rootUserId, tUserFallback: t, displayName
} = useUser(user);
const { t: tI18n } = useI18n();
const { isGuest: isCurrentUserGuest, isFreelancer: isCurrentUserFreelancer, isAdmin: isCurrentUserAdmin } = useUser('self');
const { isMobileWidth, isSidebarVisible } = useResponsiveness();
const { countries } = useAddressTools();
const { createNewChat, isOpened } = useMessengerState();
const router = useRouter();

const el = templateRef<HTMLElement>('el');
const avatarRerenderKey = ref<number>(0);
const isRecommendationsOpen = ref(false);
const avatarDialog = ref<boolean>(false);
const isBasicBusy = ref(false);
const basicLoadingAnimation = 1;
const basicLoadingAnimationSize = 50;

const isAvailableForRecommendation = computed(() => availability.value?.available || isCurrentUserAdmin.value);
const showProfileInfos = computed(() => !showContactHistory.value && !showBadgesLegend.value);
const userInfo = computed(() => store.getters['userStates/getUserInfo'](user.value?.userId ?? 'user'));
const memberSince = computed(() => getFormattedDate(profile.value?.basic.memberSince));
const avatarSize = computed(() => (isMobileWidth.value ? 60 : 120));
const isBot = computed(() => BOT_NAMES.includes(user.value?.fullname ?? ''));
const canWrite = computed(() => !(isSelf.value || isBot.value));
const badgesTheme = computed(() => (isQuickInfo && isMobileWidth.value ? 'dark' : 'bright'));
const canRecommend = computed(
  () => (!isSidebarVisible.value || isQuickInfo)
      && ((!isSelf.value && !isCustomer.value && !isEmployee.value && !isGuest.value && isCurrentUserFreelancer.value)
        || isCurrentUserAdmin.value)
);
const canEditAvatar = computed(() => canEdit.value && !isMobileWidth.value && !isCurrentUserGuest.value);

const {
  hasUnresolvedFor, discardSection, avatarPreview, previewProfile
} = useProfilePreview();
const {
  isReanalyzing,
  activeReanalyzeSection,
  hasReanalyzeSource,
  reanalyzeSection,
  cancelReanalyze,
} = useProfilePdfUpload();
const { isBannerDismissed } = useProfilePdfUploadPopup();
const hasBasicPreview = computed(() => hasUnresolvedFor('basic'));
const showProfilePdfBanner = computed(() => {
  const {
    missingSummary,
    missingSkills,
    missingExperiences,
  } = store.state.dashboard.userProfileStatus;

  return canEdit.value
    && store.state.dashboard.userProfileStatusLoaded
    && !isBannerDismissed.value
    && (missingSummary || missingSkills || missingExperiences);
});
const showAssistantButton = computed(
  () => canEdit.value
    && store.state.dashboard.userProfileStatusLoaded
    && isSectionFilled.value.basic
    && !hasBasicPreview.value
    && !showProfilePdfBanner.value,
);
const showExtraBottomGap = computed(() => canEdit.value && !isQuickInfo && !showAssistantButton.value);
const showBasicBar = computed(() => canEdit.value && hasBasicPreview.value);
const isActiveReanalyzeBar = computed(() => (
  isReanalyzing.value && activeReanalyzeSection.value === 'basic'
));
const avatarPreviewSrc = computed(() => (
  avatarPreview.value ? `data:${avatarPreview.value.imageType};base64,${avatarPreview.value.imageData}` : ''
));

const resolveReanalyzePdfSource = (): ProfilePdfReanalyzeSource => {
  const previewExperiences = previewProfile.value.experiences;
  const savedExperiences = savedProfile.value?.experiences;

  return {
    pdfBase64: previewExperiences?.experiencesDocument ?? savedExperiences?.experiencesDocument ?? '',
    pdfFileName: previewExperiences?.experiencesDocumentLabel ?? savedExperiences?.experiencesDocumentLabel ?? '',
  };
};
const hasReanalyzeFallbackSource = computed(() => {
  const { pdfBase64, pdfFileName } = resolveReanalyzePdfSource();
  return !!pdfBase64 && !!pdfFileName;
});
const disableNewSuggestion = computed(() => {
  if (isActiveReanalyzeBar.value) return false;
  return (
    (isReanalyzing.value && activeReanalyzeSection.value !== 'basic')
    || (!hasReanalyzeSource.value && !hasReanalyzeFallbackSource.value)
  );
});

const resetAvatarCache = () => {
  if (!user.value?.userId) return;
  store.commit('avatarCache/resetUserState', 'user');
  store.commit('avatarCache/resetUserState', user.value.userId);
};

const { globalErrorMessage } = usePageState();
const resolveCountryIsoCode = (country?: string) => {
  if (!country) return undefined;
  const resolved = getValueByText(countries.value, country)?.toString();
  return resolved || country;
};

const resolveCityForBasicUpdate = async (countryIsoCode?: string, city?: string) => {
  if (!countryIsoCode || !city) return '';

  const filter = extractCityName(city).trim();
  if (!filter) return '';

  const citiesResult = await getCountryCities(countryIsoCode, filter);
  const firstMatch = citiesResult[0];
  if (!firstMatch) return '';

  return `${firstMatch.postalCode}, ${firstMatch.placeName}`;
};

const onAcceptBasic = async () => {
  isBasicBusy.value = true;

  try {
    const mergedBasic = profile.value?.basic;

    if (previewProfile.value.basic && mergedBasic) {
      const basicUpdate = await buildAcceptedBasicUpdate(
        previewProfile.value.basic,
        mergedBasic,
        resolveCountryIsoCode,
        resolveCityForBasicUpdate,
      );

      if (basicUpdate) {
        await updateBasicData(basicUpdate);
      }
    }

    if (avatarPreview.value) {
      const normalizedAvatar = await normalizeAvatarImage(
        avatarPreview.value.imageData,
        avatarPreview.value.imageType,
      );
      await updateAvatar(normalizedAvatar.imageData, normalizedAvatar.imageType);
      resetAvatarCache();
    }

    try {
      await loadProfile({ silent: true });
    } catch (error) {
      globalErrorMessage.value = {
        i18Key: 'profile.common.reloadFailedAfterSave',
        code: getAxiosResponseErrorCode(error as AxiosError) ?? 'UNKNOWN_ERROR',
        component: 'ProfileBasicView.vue',
        withoutReload: true,
        timeoutMs: 15000,
      };
      return;
    }
    discardSection('basic');
  } catch (e) {
    globalErrorMessage.value = {
      i18Key: 'profilePdfUpload.errors.acceptFailed',
      code: getAxiosResponseErrorCode(e as AxiosError),
      component: 'ProfileBasicView.vue',
      withoutReload: true,
      timeoutMs: 15000,
    };
    logError('Failed to accept basic profile preview', e);
  } finally {
    isBasicBusy.value = false;
  }
};
const onEditBasic = () => {
  toggleEditSection('basic');
};
const onDiscardBasic = () => {
  discardSection('basic');
};
const onNewSuggestion = async () => {
  await reanalyzeSection('basic', resolveReanalyzePdfSource());
};
const showRecommmendation = computed(() => canRecommend.value && canWrite.value);
const showCompanyBanner = computed(() => (isGuest.value || isEmployee.value) && isQuickInfo);
const isAvailabilityVisible = computed(() => isSelf.value && isMobileWidth.value);
const displayCity = computed(() => extractCityName(profile.value?.basic.city ?? ''));
const displayCountry = computed(() => {
  const rawCountry = profile.value?.basic.country ?? '';
  if (!rawCountry) return '';
  if (rawCountry.length === 2) {
    return tI18n(`user-search.filters.countries.${rawCountry}`).toString();
  }
  return getTextByValue(countries.value, rawCountry) || rawCountry;
});

const writeMessage = async () => {
  if (!user.value) return;

  isOpened.value = true;
  await createNewChat(user.value);

  if (!isMobileWidth.value) return;
  setQuickInfoUser(null);
};

const edit = () => toggleEditSection('basic');

const gotoProfile = async () => {
  setQuickInfoUser(null);
  if (isMobileWidth.value) {
    isOpened.value = false;
  }

  const path = isSelf.value ? '/profile' : `/profile/${user.value?.userId}`;

  if (props.target === '_blank') {
    const routeData = router.resolve({ path });
    window.open(routeData.href, '_blank');
  } else {
    await router.push({ path });
  }
};

const showQuickInfo = () => {
  if (!el.value?.parentElement) return;
  showQuickInfoOriginal(el.value.parentElement, user, 'contact-history');
};

const badgeClicked = () => {
  if (!el.value?.parentElement) return;
  showQuickInfoOriginal(el.value.parentElement, user, 'badges-legend');
};

const recommendProject = () => {
  isRecommendationsOpen.value = true;
};

const openAvailability = () => {
  isAvailabilityPanelOpen.value = !isAvailabilityPanelOpen.value;
};

return { __sfc: true,props, showContactHistory, showBadgesLegend, emit, setQuickInfoUser, showQuickInfoOriginal, availability, getFormattedDate, isAvailabilityPanelOpen, user, profile, isSelf, toggleEditSection, isQuickInfo, canEdit, isSectionFilled, loadProfile, savedProfile, isCustomer, isGuest, isEmployee, rootUserId, t, displayName, tI18n, isCurrentUserGuest, isCurrentUserFreelancer, isCurrentUserAdmin, isMobileWidth, isSidebarVisible, countries, createNewChat, isOpened, router, el, avatarRerenderKey, isRecommendationsOpen, avatarDialog, isBasicBusy, basicLoadingAnimation, basicLoadingAnimationSize, isAvailableForRecommendation, showProfileInfos, userInfo, memberSince, avatarSize, isBot, canWrite, badgesTheme, canRecommend, canEditAvatar, hasUnresolvedFor, discardSection, avatarPreview, previewProfile, isReanalyzing, activeReanalyzeSection, hasReanalyzeSource, reanalyzeSection, cancelReanalyze, isBannerDismissed, hasBasicPreview, showProfilePdfBanner, showAssistantButton, showExtraBottomGap, showBasicBar, isActiveReanalyzeBar, avatarPreviewSrc, resolveReanalyzePdfSource, hasReanalyzeFallbackSource, disableNewSuggestion, resetAvatarCache, globalErrorMessage, resolveCountryIsoCode, resolveCityForBasicUpdate, onAcceptBasic, onEditBasic, onDiscardBasic, onNewSuggestion, showRecommmendation, showCompanyBanner, isAvailabilityVisible, displayCity, displayCountry, writeMessage, edit, gotoProfile, showQuickInfo, badgeClicked, recommendProject, openAvailability, BCXAvatar, BCXItemsWithPoints, BCXMarkdown, BCXPoints, BCXUserBadges, BCXUserBanner, EditIcon, ProfileSectionBusyOverlay, IconAvatarEdit, MessageIcon, ShareIcon, UserHistoryIcon, UserIcon, FlagIcon, Reporting, AvatarUpload, ProfileAssistantButton, AiSuggestionReviewBar, RecommendUserForProjectsMobileLayer, RecommendUserForProjectsQuickInfoLayer, UserAvailability, BCXUserBannerSimple }
}

})